import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { PhoneCall, PhoneIncoming, PhoneMissed, PhoneOff, Loader2, Users } from "lucide-react";
import AppShell from "../components/app/AppShell";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { userLocale } from "../utils/locale";

/**
 * RC-WEB-4 §16 / RC-WEB-5 GATE 9 — canonical call activity (`GET /api/inbox/calls`).
 * Only states backed by real records are rendered; dates use the reader locale.
 */

const API = "/api";

const ICONS = {
  answered: PhoneCall,
  ringing: PhoneIncoming,
  pending: PhoneIncoming,
  requested: PhoneIncoming,
  missed: PhoneMissed,
  declined: PhoneOff,
  cancelled: PhoneOff,
  ended: PhoneCall,
};

const CLASSES = {
  answered: "bg-emerald-100 text-emerald-800",
  ringing: "bg-[#1B84FF]/15 text-[#0B2545]",
  pending: "bg-slate-100 text-slate-700",
  requested: "bg-slate-100 text-slate-700",
  missed: "bg-red-100 text-red-800",
  declined: "bg-amber-100 text-amber-900",
  cancelled: "bg-slate-100 text-slate-700",
  ended: "bg-slate-100 text-slate-700",
};

const Calls = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const locale = userLocale(lang, user?.country_code);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/inbox/calls`)
      .then((r) => { if (alive) setCalls(r.data?.calls || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <AppShell title={t("app.calls.title")} subtitle={t("app.calls.subtitle")} maxWidth="max-w-4xl">
      {loading ? (
        <div className="flex justify-center py-16" data-testid="calls-loading">
          <Loader2 className="h-6 w-6 animate-spin text-[#1B84FF]" />
        </div>
      ) : calls.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="calls-empty-state">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
            <PhoneCall className="h-8 w-8 text-slate-600" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-[#0B2545]">{t("app.calls.empty_title")}</h3>
          <p className="mx-auto mb-6 max-w-md text-sm text-slate-600">{t("app.calls.empty_text")}</p>
          <Link to="/contactos-de-confianza">
            <Button variant="outline" className="border-slate-300 text-[#0B2545]" data-testid="calls-empty-trusted-btn">
              <Users className="mr-2 h-4 w-4" /> {t("app.calls.manage_trusted")}
            </Button>
          </Link>
        </div>
      ) : (
        <ul className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="calls-list">
          {calls.map((c) => {
            const Icon = ICONS[c.state] || PhoneIncoming;
            return (
              <li key={c.id} className="flex items-center gap-3 px-4 py-3.5" data-testid={`call-row-${c.id}`}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#0B2545]">
                    {c.profile_name || t("app.inbox.protection")}
                  </p>
                  <p className="text-xs text-slate-600">
                    {c.requested_at ? new Date(c.requested_at).toLocaleString(locale) : "—"}
                    {c.answered_by
                      ? ` · ${t("app.calls.answered_by")}: ${t(`app.calls.who.${c.answered_by}`)}`
                      : ""}
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${CLASSES[c.state] || CLASSES.pending}`}
                      data-testid={`call-state-${c.id}`}>
                  {t(`app.calls.state.${c.state}`)}
                </span>
                <Link to={`/mensajes?c=${c.conversation_id}`} className="shrink-0 text-xs font-semibold text-[#1B84FF] hover:underline">
                  {t("app.calls.view")}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </AppShell>
  );
};

export default Calls;
